import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { adminFetch } from '@/lib/adminFetch';

export type AdminRole = 'Super Admin' | 'Editor' | 'Viewer';

export interface AdminAuthContextType {
  user: any | null;
  role: AdminRole | null;
  isAuthorized: boolean;
  isSuperAdmin: boolean;
  isEditor: boolean;
  isViewer: boolean;
  loading: boolean;
  authError: string | null;
  loginWithGoogle: () => Promise<void>;
  loginWithMicrosoft: () => Promise<void>;
  signOut: () => Promise<void>;
  clearError: () => void;
  ensureAuthListener: () => Promise<void>;
}

export const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined);

const MASTER_OWNER_EMAIL = 'gordon.marc@gmail.com';

function useCurrentPath(): string {
  try {
    const loc = useLocation();
    return loc?.pathname || '';
  } catch {
    if (typeof window !== 'undefined') {
      return window.location.pathname;
    }
    return '';
  }
}

export function isAuthRequiredRoute(pathname: string): boolean {
  if (!pathname) return false;
  const norm = pathname.toLowerCase().replace(/\/+$/, '') || '/';

  // 1. /admin* (all admin portal routes)
  if (norm === '/admin' || norm.startsWith('/admin/')) return true;

  // 2. /practitioner* auth routes (e.g. /practitioner/login, /practitioner/dashboard, /practitioner/*)
  // Exclude public practitioner pages (/practitioner-certification, /practitioners, /practitioner-directory, etc.)
  if (norm === '/practitioner' || norm.startsWith('/practitioner/')) return true;

  // 3. /portal* & /client-portal*
  if (norm === '/portal' || norm.startsWith('/portal/') || norm === '/client-portal' || norm.startsWith('/client-portal/')) return true;

  // 4. /diagnostic-results* & /diagnostic/results & /mini-assessment/results & /mini-assessment-results
  if (
    norm === '/diagnostic-results' ||
    norm.startsWith('/diagnostic-results/') ||
    norm === '/diagnostic/results' ||
    norm === '/mini-assessment/results' ||
    norm === '/mini-assessment-results'
  ) return true;

  // 5. /payment* (e.g. /payment/success)
  if (norm === '/payment' || norm.startsWith('/payment/')) return true;

  // 6. /commission* (e.g. /commission, /commissions)
  if (norm === '/commission' || norm.startsWith('/commission/')) return true;

  return false;
}

export function AdminAuthProvider({ children }: { children: React.ReactNode }) {
  const currentPath = useCurrentPath();
  const initialNeedsAuth = typeof window !== 'undefined' ? isAuthRequiredRoute(currentPath) : false;

  const [user, setUser] = useState<any | null>(null);
  const [role, setRole] = useState<AdminRole | null>(null);
  const [isAuthorized, setIsAuthorized] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(initialNeedsAuth);
  const [authError, setAuthError] = useState<string | null>(null);

  const listenerStartedRef = useRef(false);
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const isMountedRef = useRef(true);

  // Check user authorization against master owner and server-side verification endpoint only
  const checkAuthorization = async (currentUser: any | null): Promise<{ authorized: boolean; role: AdminRole | null }> => {
    if (!currentUser || !currentUser.email) {
      return { authorized: false, role: null };
    }

    const email = currentUser.email.toLowerCase().trim();

    // 1. Master Owner inherent Super Admin
    if (email === MASTER_OWNER_EMAIL) {
      return { authorized: true, role: 'Super Admin' };
    }

    // 2. Query server-side verification endpoint using authenticated token (Authorisation comes only from /api/admin/verify)
    try {
      const res = await adminFetch('/api/admin/verify');
      if (res.ok) {
        const data = await res.json();
        if (data.authorized) {
          return { authorized: true, role: data.role as AdminRole };
        }
      }
    } catch (apiErr) {
      console.warn('Notice querying /api/admin/verify:', apiErr);
    }

    return { authorized: false, role: null };
  };

  const setAdminCookie = (active: boolean) => {
    if (typeof document !== 'undefined') {
      if (active) {
        document.cookie = "admin_session=1; path=/; max-age=2592000; SameSite=Lax";
      } else {
        document.cookie = "admin_session=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax";
      }
    }
  };

  const startAuthListener = useCallback(async () => {
    if (listenerStartedRef.current) return;
    listenerStartedRef.current = true;
    setLoading(true);

    try {
      if (typeof window !== "undefined") {
        localStorage.removeItem("fivelens_admin_dev_session");
      }

      const { auth } = await import('@/lib/firebase');
      const { onAuthStateChanged } = await import('firebase/auth');

      if (!isMountedRef.current) return;

      unsubscribeRef.current = onAuthStateChanged(auth, async (currentUser) => {
        if (!isMountedRef.current) return;
        setLoading(true);
        setAuthError(null);

        if (currentUser) {
          const { authorized, role: assignedRole } = await checkAuthorization(currentUser);
          if (!isMountedRef.current) return;
          setUser(currentUser);
          setIsAuthorized(authorized);
          setRole(assignedRole);
          setAdminCookie(authorized);
        } else {
          setUser(null);
          setIsAuthorized(false);
          setRole(null);
          setAdminCookie(false);
        }

        setLoading(false);
      });
    } catch (err) {
      if (isMountedRef.current) setLoading(false);
    }
  }, []);

  // Listen to path changes - start auth listener only when on an auth-required route
  useEffect(() => {
    if (isAuthRequiredRoute(currentPath)) {
      startAuthListener();
    }
  }, [currentPath, startAuthListener]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, []);

  const loginWithGoogle = async () => {
    startAuthListener();
    setLoading(true);
    setAuthError(null);
    try {
      const { auth, googleProvider } = await import('@/lib/firebase');
      const { signInWithPopup } = await import('firebase/auth');
      const result = await signInWithPopup(auth, googleProvider);
      const { authorized, role: assignedRole } = await checkAuthorization(result.user);
      setUser(result.user);
      setIsAuthorized(authorized);
      setRole(assignedRole);
      setAdminCookie(authorized);
    } catch (err: any) {
      console.error('Google Sign-In error:', err);
      let msg = err?.message || 'Google sign-in failed.';
      if (err.code === 'auth/unauthorized-domain') {
        msg = `Domain not authorized. Please add ${window.location.hostname} in Firebase Console > Authentication > Settings > Authorized domains.`;
      }
      setAuthError(msg);
    } finally {
      setLoading(false);
    }
  };

  const loginWithMicrosoft = async () => {
    startAuthListener();
    setLoading(true);
    setAuthError(null);
    try {
      const { auth, microsoftProvider } = await import('@/lib/firebase');
      const { signInWithPopup } = await import('firebase/auth');
      const result = await signInWithPopup(auth, microsoftProvider);
      const { authorized, role: assignedRole } = await checkAuthorization(result.user);
      setUser(result.user);
      setIsAuthorized(authorized);
      setRole(assignedRole);
      setAdminCookie(authorized);
    } catch (err: any) {
      console.error('Microsoft Sign-In error:', err);
      let msg = err?.message || 'Microsoft sign-in failed.';
      if (err.code === 'auth/unauthorized-domain') {
        msg = `Domain not authorized. Please add ${window.location.hostname} in Firebase Console > Authentication > Settings > Authorized domains.`;
      }
      setAuthError(msg);
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    if (typeof window !== 'undefined') {
      localStorage.removeItem("fivelens_admin_dev_session");
    }
    setAdminCookie(false);
    try {
      const { auth } = await import('@/lib/firebase');
      const { signOut: firebaseSignOut } = await import('firebase/auth');
      await firebaseSignOut(auth);
    } catch (err) {
      console.warn('Sign-out notice:', err);
    }
    setUser(null);
    setIsAuthorized(false);
    setRole(null);
  };

  return (
    <AdminAuthContext.Provider
      value={{
        user,
        role,
        isAuthorized,
        isSuperAdmin: role === 'Super Admin',
        isEditor: role === 'Editor',
        isViewer: role === 'Viewer',
        loading,
        authError,
        loginWithGoogle,
        loginWithMicrosoft,
        signOut,
        clearError: () => setAuthError(null),
        ensureAuthListener: startAuthListener,
      }}
    >
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
}
